/**
 * Wizard State Atoms
 * Template UX Enhancement: Wizard-based template flows
 */

import { atom } from 'jotai';
import { Template, TemplateScene, TemplateInputDefinition } from '../../../types/template';

export type Platform = 'youtube' | 'linkedin' | 'instagram' | 'tiktok' | 'twitter' | 'course' | 'internal' | 'multiple';
export type Audience = 'customers' | 'investors' | 'students' | 'team' | 'public' | 'developers';
export type Goal = 'teach' | 'demo' | 'sell' | 'introduce' | 'document' | 'testimonial';
export type Duration = 'quick' | 'standard' | 'detailed' | 'longform';
export type WizardView = 'gallery' | 'make-it-yours' | 'smart-wizard' | 'classic-editor' | null;

export type SceneAIStatus = 'idle' | 'generating' | 'generated' | 'error';

export interface CustomizedScene {
  order: number;
  title: string;
  duration: number;
  script: string;
  templateScript: string;  // Original template script for AI adaptation
  sceneNotes?: string;     // Scene notes/tips from template
  layoutPreset: string;
  isCustomized: boolean;
  aiStatus: SceneAIStatus;
  aiDraft?: string;
}

export type ContentTone = 'professional' | 'casual' | 'inspiring' | 'educational';

export interface SmartWizardContext {
  platform: Platform | null;
  audience: Audience | null;
  goal: Goal | null;
  duration: Duration | null;
}

export interface MakeItYoursState {
  baseTemplateId: string;
  baseTemplate: Template | null;
  currentStep: 1 | 2;
  topic: string;
  audienceLevel: 'beginner' | 'intermediate' | 'advanced';
  tone: ContentTone;
  keyPoints: string;
  callToAction: string;
  scenes: CustomizedScene[];
  aspectRatio: '16:9' | '9:16' | '1:1';
  sourcePlatform: Platform | null;
  smartWizardContext: SmartWizardContext;
  features: {
    teleprompter: boolean;
    coaching: boolean;
    noiseSuppression: boolean;
  };
  isGeneratingAll: boolean;
  hasGeneratedOnce: boolean;
  inputDefinitions: TemplateInputDefinition[];
  dynamicInputs: Record<string, string>;
}

export interface TemplateRecommendation {
  templateId: string;
  template: Template;
  score: number;
  matchReasons: string[];
}

export interface SmartWizardState {
  currentStep: 1 | 2 | 3 | 4 | 5;
  platform: Platform | null;
  audience: Audience | null;
  goal: Goal | null;
  duration: Duration | null;
  recommendations: TemplateRecommendation[];
  isCalculating: boolean;
}

export const wizardViewAtom = atom<WizardView>(null);

export const makeItYoursStateAtom = atom<MakeItYoursState | null>(null);

export const smartWizardStateAtom = atom<SmartWizardState>({
  currentStep: 1,
  platform: null,
  audience: null,
  goal: null,
  duration: null,
  recommendations: [],
  isCalculating: false
});

const getAspectRatioForPlatform = (platform: Platform | null): '16:9' | '9:16' | '1:1' => {
  switch (platform) {
    case 'youtube':
    case 'linkedin':
    case 'course':
    case 'internal':
      return '16:9';
    case 'tiktok':
    case 'instagram':
      return '9:16';
    case 'twitter':
      return '1:1';
    default:
      return '16:9';
  }
};

export const initMakeItYoursAtom = atom(
  null,
  (get, set, template: Template, sourcePlatform?: Platform | null) => {
    const smartWizardState = get(smartWizardStateAtom);
    const platform = sourcePlatform ?? smartWizardState.platform ?? null;
    
    const scenes: CustomizedScene[] = template.scenes.map((scene, index) => {
      const originalScript = template.scripts.find(s => s.key === scene.scriptKey)?.content || '';
      return {
        order: index,
        title: scene.title,
        duration: scene.suggestedDuration,
        script: originalScript,
        templateScript: originalScript,  // Preserve original for AI adaptation
        sceneNotes: scene.notes,          // Pass through scene notes if available
        layoutPreset: scene.layoutPreset,
        isCustomized: false,
        aiStatus: 'idle' as SceneAIStatus,
        aiDraft: undefined
      };
    });

    const aspectRatio = platform 
      ? getAspectRatioForPlatform(platform)
      : (template.aspectRatio as '16:9' | '9:16' | '1:1') || '16:9';

    const inputDefinitions = template.customization?.inputs || [];
    const dynamicInputs: Record<string, string> = {};
    inputDefinitions.forEach(input => {
      dynamicInputs[input.id] = input.defaultValue || '';
    });

    set(makeItYoursStateAtom, {
      baseTemplateId: template.id,
      baseTemplate: template,
      currentStep: 1,
      topic: '',
      audienceLevel: 'intermediate',
      tone: 'professional',
      keyPoints: '',
      callToAction: '',
      scenes,
      aspectRatio,
      sourcePlatform: platform,
      smartWizardContext: {
        platform: smartWizardState.platform,
        audience: smartWizardState.audience,
        goal: smartWizardState.goal,
        duration: smartWizardState.duration
      },
      features: {
        teleprompter: true,
        coaching: true,
        noiseSuppression: true
      },
      isGeneratingAll: false,
      hasGeneratedOnce: false,
      inputDefinitions,
      dynamicInputs
    });
    set(wizardViewAtom, 'make-it-yours');
  }
);

export const resetSmartWizardAtom = atom(
  null,
  (get, set) => {
    set(smartWizardStateAtom, {
      currentStep: 1,
      platform: null,
      audience: null,
      goal: null,
      duration: null,
      recommendations: [],
      isCalculating: false
    });
    set(wizardViewAtom, 'smart-wizard');
  }
);

export const closeWizardAtom = atom(
  null,
  (get, set) => {
    set(wizardViewAtom, null);
    set(makeItYoursStateAtom, null);
  }
);
